import { Fragment, useContext, useMemo } from "react";
import { Menu, Transition } from "@headlessui/react";

import { useCompanyStore, useDisclosureStore, useHelperStore, useTabStore } from "~/stores";
import useDefaultAllDisclosures from "~/features/SearchV3/hooks/useDefaultAllDisclosures";
import { LanguageContext } from "~/providers/LanguageProvider";

import DisclosuresRenderButton from "./DisclosuresRenderButton";
import DisclosuresMenuItem from "./DisclosuresMenuItem";

import { NormalizedDisclosure, SEARCH_TABS } from "~/types/search.types";
import { SearchActions } from "~/types/actions.enum";
import { TSetActiveDisclosure } from "~/stores/disclosure/disclosure.state";

/** This disclosures menu only reads from the fetched Disclosures data store */
const PopupDisclosureList = () => {
    const { language } = useContext(LanguageContext);
    const { DEFAULT_ALL_DISCLOSURES } = useDefaultAllDisclosures();

    const dispatch = useDisclosureStore(state => state.dispatch);

    const disclosure = useDisclosureStore(state => state.disclosure);
    const disclosures = useDisclosureStore(state => state.disclosures);
    const isAllDisclosuresAvailable = useDisclosureStore(state => state.isAllDisclosuresAvailable);
    const isFetching = useHelperStore(state => state.isLoading);
    const activeTab = useTabStore(state => state.tab);
    const totalCompanies = useCompanyStore(state => state.totalSelected);
    const disableUpdate = activeTab?.key === SEARCH_TABS.FULL_DOCUMENTS && totalCompanies > 0;
    const isFullDocuments = activeTab?.key === SEARCH_TABS.FULL_DOCUMENTS;

    const fullDisclosuresList = isFullDocuments
        ? disclosures
        : [DEFAULT_ALL_DISCLOSURES, ...disclosures];

    const disclosureName = useMemo(() => {
        const isAllDisclosuresAvailableName = isAllDisclosuresAvailable?.label[language];
        const itemName = disclosure?.label[language];

        return isAllDisclosuresAvailable && !isFullDocuments
            ? isAllDisclosuresAvailableName
            : itemName;
    }, [isAllDisclosuresAvailable, disclosure]);

    const handleBtnClick = (item: NormalizedDisclosure) => {
        const isAllDisclosure = item?.id === -1;

        if (isAllDisclosure) {
            dispatch({
                type: SearchActions.SET_IS_ALL_DISCLOSURES,
                payload: DEFAULT_ALL_DISCLOSURES
            });
        } else {
            const disclosureParams = {
                property: "exploreId",
                value: item.exploreId
            } as TSetActiveDisclosure;
            dispatch({
                type: SearchActions.SET_ACTIVE_DISCLOSURE_BY_KEY,
                payload: disclosureParams
            });
        }
    };

    const listItems = useMemo(() => {
        return fullDisclosuresList.map(item => {
            const isSelected =
                isAllDisclosuresAvailable && !isFullDocuments
                    ? item.id === -1
                    : item.id === disclosure?.id;

            return (
                <DisclosuresMenuItem
                    key={item.id}
                    disclosure={item}
                    isSelected={isSelected}
                    btnClickCallback={handleBtnClick}
                />
            );
        });
    }, [disclosures, disclosure, isAllDisclosuresAvailable]);

    return (
        <Menu as="div" className="relative inline-block bg-white !z-1">
            <Menu.Button className="focus:outline-none" disabled={isFetching || disableUpdate}>
                {({ open }) => (
                    <DisclosuresRenderButton open={open} disclosureName={disclosureName ?? null} />
                )}
            </Menu.Button>

            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <Menu.Items className="absolute mt-2 w-56 origin-top-right border border-[rgba(166, 160, 155, 0.20)] rounded-md bg-white !shadow-md focus:outline-none z-10">
                    <div className="px-1 py-1">{listItems}</div>
                </Menu.Items>
            </Transition>
        </Menu>
    );
};

export default PopupDisclosureList;
